import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Check, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDate } from "@/lib/format";
import { Card, Loading, PageHeader, Button, Badge, EmptyState } from "@/components/common";

const ST = { submitted: { label: "Menunggu Approval", c: "bg-amber-100 text-amber-700" }, approved: { label: "Disetujui", c: "bg-emerald-100 text-emerald-700" }, rejected: { label: "Ditolak", c: "bg-rose-100 text-rose-700" } };

export default function OpnameApproval() {
  const { branch, user, can, isOwner } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => { if (!branch) return; setData(null); api.get("/opname/audit-log", { branch_id: branch.id }).then(setData).catch((e) => toast.error(e.message)); };
  useEffect(load, [branch]);

  const decide = async (o, action, notes = "") => {
    setBusy(o.id);
    try { await api.post(`/opname/${o.id}/${action}`, { notes }); toast.success(action === "approve" ? "Disetujui & stok diperbarui" : "Ditolak"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const reject = (o) => { const r = window.prompt("Alasan menolak hasil audit:"); if (r && r.trim()) decide(o, "reject", r.trim()); };
  const del = async (o) => { const r = window.prompt(`Hapus ${o.opname_no}? Masukkan alasan (khusus Owner):`); if (!r || !r.trim()) return; try { await api.del(`/opname/${o.id}`, { reason: r.trim() }); toast.success("Dihapus"); load(); } catch (e) { toast.error(e.message); } };

  if (!can("stock_opname", "approve")) return (<><PageHeader title="Approval Stok Opname" icon={ClipboardCheck} /><EmptyState icon={Lock} title="Akses terbatas" message="Modul ini hanya untuk Owner/Manager dengan hak akses Approve Stock Opname." /></>);

  const rows = (data?.rows || []).filter((r) => !filter || r.status === filter);
  return (
    <>
      <PageHeader title="Approval Stok Opname" subtitle={`Owner / Manager · ${branch?.name || ""}`} icon={ClipboardCheck} />
      {!data ? <Loading /> : (
        <>
          <h3 className="font-semibold text-slate-700 mb-2">Menunggu Persetujuan ({data.pending.length})</h3>
          {data.pending.length === 0 && <Card className="p-4 mb-4"><p className="text-sm text-slate-400">Tidak ada hasil audit yang menunggu persetujuan.</p></Card>}
          <div className="space-y-2 mb-6">
            {data.pending.map((o) => {
              const own = o.created_by_name === user?.name;
              return (
                <Card key={o.id} className="p-4 border-amber-300" data-testid={`approval-${o.opname_no}`}>
                  <div className="flex justify-between items-center"><span className="text-xs text-slate-500 font-medium">{o.opname_no} · {fmtDate(o.submitted_at)}</span><Badge className="bg-amber-100 text-amber-700">PENDING</Badge></div>
                  <div className="font-medium text-slate-800 mt-1">Pemeriksa: {o.created_by_name} · {o.items} item</div>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <Sum n={o.summary.match} l="Pas" c="emerald" />
                    <Sum n={o.summary.over} l={`Kelebihan (+${num(o.summary.over_qty)})`} c="sky" />
                    <Sum n={o.summary.short} l={`Kekurangan (−${num(o.summary.short_qty)})`} c="rose" />
                  </div>
                  <div className={`text-sm mt-2 ${o.total_variance_value < 0 ? "text-rose-600" : "text-emerald-600"}`}>Nilai selisih (HPP): {rupiah(o.total_variance_value)}</div>
                  {own ? <div className="text-xs text-amber-600 mt-1">Anda pemeriksa audit ini — Owner/Manager lain yang harus menyetujui.</div> : (
                    <div className="flex gap-2 mt-3">
                      <Button variant="outline" onClick={() => nav("/opname")} data-testid={`approval-review-${o.id}`}>Tinjau</Button>
                      <Button variant="danger" onClick={() => reject(o)} data-testid={`approval-reject-${o.id}`}>Tolak</Button>
                      <Button className="flex-1" onClick={() => decide(o, "approve")} disabled={busy === o.id} data-testid={`approval-approve-${o.id}`}><Check size={16} /> Setujui & Update Stok</Button>
                    </div>
                  )}
                  {isOwner && <div className="flex gap-2 mt-2"><Button variant="outline" onClick={() => nav("/opname")} data-testid={`approval-edit-${o.id}`}>Edit / Revisi</Button><Button variant="danger" onClick={() => del(o)} data-testid={`approval-delete-${o.id}`}>Hapus</Button></div>}
                </Card>
              );
            })}
          </div>

          <h3 className="font-semibold text-slate-700 mb-2">Riwayat Log Audit</h3>
          <div className="flex flex-wrap gap-2 mb-3">
            {[["", "Semua"], ["submitted", "Pending"], ["approved", "Disetujui"], ["rejected", "Ditolak"]].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} data-testid={`approval-filter-${k || "all"}`} className={`px-3 h-8 rounded-full text-xs font-medium ${filter === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
          </div>
          {rows.length === 0 ? <EmptyState icon={ClipboardCheck} title="Belum ada log" message="Belum ada log audit." /> : (
            <Card className="overflow-hidden" data-testid="approval-log">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 bg-slate-50/60 border-b border-slate-100"><th className="py-3 px-4 font-medium">Tanggal · Pemeriksa · Barang</th><th className="py-3 px-4 font-medium text-right">Sistem</th><th className="py-3 px-4 font-medium text-right">Fisik</th><th className="py-3 px-4 font-medium text-right">Selisih</th></tr></thead>
                <tbody>
                  {rows.map((r, i) => {
                    const st = ST[r.status] || { label: r.status, c: "bg-slate-100 text-slate-500" }; const v = r.variance || 0;
                    return (
                      <tr key={`${r.opname_id}-${r.item_name}-${i}`} className={`border-t border-slate-50 ${v < 0 ? "bg-rose-50/40" : v > 0 ? "bg-sky-50/40" : ""}`} data-testid={`approval-log-row-${i}`}>
                        <td className="py-3 px-4"><div className="text-xs text-slate-400">{fmtDate(r.date)} · {r.counted_by}</div><div className="font-medium text-slate-800">{r.item_name}</div><div className="flex items-center gap-2 mt-0.5"><Badge className={st.c}>{st.label}</Badge><span className="text-xs text-slate-400">{r.opname_no}{r.approved_by_name ? ` · ${r.approved_by_name}` : ""}{r.reason ? ` · ${r.reason}` : ""}</span></div></td>
                        <td className="py-3 px-4 text-right">{r.qty_system == null ? "-" : num(r.qty_system)}</td>
                        <td className="py-3 px-4 text-right">{r.qty_counted == null ? "-" : num(r.qty_counted)}</td>
                        <td className={`py-3 px-4 text-right font-bold ${v < 0 ? "text-rose-600" : v > 0 ? "text-sky-600" : "text-emerald-600"}`}>{v > 0 ? "+" : ""}{num(v)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            </Card>
          )}
        </>
      )}
    </>
  );
}

const SUMC = { emerald: { b: "border-emerald-300", t: "text-emerald-600" }, sky: { b: "border-sky-300", t: "text-sky-600" }, rose: { b: "border-rose-300", t: "text-rose-600" } };
const Sum = ({ n, l, c }) => (<div className={`border rounded-lg p-2 text-center bg-slate-50 ${SUMC[c].b}`}><div className={`text-xl font-bold ${SUMC[c].t}`}>{n}</div><div className="text-[10px] text-slate-400">{l}</div></div>);
